import { FC } from 'react';
import { Box, Skeleton, Svg, Text } from '@hopskipdrive/react-components';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import NotInterestedRoundedIcon from '@mui/icons-material/NotInterestedRounded';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';

import { ContactCard } from '../../../../../shared/components/ContactCard';
import { CopyToClipboard } from '../../../../../shared/components/CopyToClipboard';
import { colorTokens } from '../../../../../shared/components/variables';

interface RiderContactProps {
  slug: string;
  name: string;
  phone: string;
  email: string;
  contactRole: string;
  permissions: { name: string; value: boolean }[];
}

export const RiderContact: FC<RiderContactProps> = ({ slug, name, phone, email, contactRole, permissions }) => {
  return (
    <ContactCard>
      <ContactCard.Title>
        <MainHeading isFetching={false} slug={slug} name={name} contactRole={contactRole} />
      </ContactCard.Title>
      <ContactCard.Content>
        <Box display="flex" flexDirection="row" alignItems="flex-start" gap="6px" flexWrap="wrap">
          {permissions.map((permission) => (
            <ContactPermissionItem key={permission.name}>
              {permission.value ? (
                <CheckIcon sx={{ color: colorTokens.success.main }} />
              ) : (
                <NotInterestedRoundedIcon sx={{ color: colorTokens.error.main }} />
              )}
              <Text>{permission.name}</Text>
            </ContactPermissionItem>
          ))}
        </Box>
        {phone && (
          <RiderInfoContainer isFetching={false}>
            <PhoneEnabledIcon style={{ color: colorTokens.text.secondary }} />
            <CopyToClipboard
              toastMessage="Phone Number Copied"
              text={phone}
              variant="h6"
              color={colorTokens.text.secondary}
            />
          </RiderInfoContainer>
        )}
        {email && (
          <RiderInfoContainer isFetching={false}>
            <EmailIcon style={{ color: colorTokens.text.secondary }} />
            <CopyToClipboard toastMessage="Email Copied" text={email} variant="h6" color={colorTokens.text.secondary} />
          </RiderInfoContainer>
        )}
      </ContactCard.Content>
    </ContactCard>
  );
};

export const SkeletonContact = () => {
  return (
    <ContactCard data-testid="skeleton-contact">
      <ContactCard.Title>
        <MainHeading isFetching={true} slug={''} />
      </ContactCard.Title>
      <ContactCard.Content>
        <Box display="flex" flexDirection="row" alignItems="flex-start" gap="6px" flexWrap="wrap">
          <ContactPermissionItem>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={100} height={20} />
          </ContactPermissionItem>
          <ContactPermissionItem>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={100} height={20} />
          </ContactPermissionItem>
          <ContactPermissionItem>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={100} height={20} />
          </ContactPermissionItem>
        </Box>

        <RiderInfoContainer isFetching={false}>
          <PhoneEnabledIcon style={{ color: colorTokens.text.secondary }} />
          <Skeleton variant="text" width={139} height={32} />
        </RiderInfoContainer>

        <RiderInfoContainer isFetching={false}>
          <EmailIcon style={{ color: colorTokens.text.secondary }} />
          <Skeleton variant="text" width={139} height={32} />
        </RiderInfoContainer>
      </ContactCard.Content>
    </ContactCard>
  );
};

interface MainHeadingProps {
  isFetching: boolean;
  slug: string;
  name?: string;
  contactRole?: string;
}

const MainHeading: FC<MainHeadingProps> = ({ isFetching, slug, name, contactRole }) => {
  return (
    <>
      <Svg icon="contact" style={{ color: colorTokens.primary.main }} />
      {isFetching ? (
        <>
          <Skeleton data-testid="main-skeleton-loader" variant="text" width={150} height={20} />
          <Skeleton variant="text" width={100} height={20} />
        </>
      ) : (
        <>
          <Text variant="h6" style={{ color: colorTokens.primary.main }}>{`${contactRole}: ${name}`}</Text>
          <CopyToClipboard toastMessage="Slug Copied" text={slug} variant="h6" color={colorTokens.text.secondary} />
        </>
      )}
    </>
  );
};

const ContactPermissionItem = ({ children }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="6px" padding="6px 9px">
      {children}
    </Box>
  );
};

const RiderInfoContainer = ({ isFetching, children }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="9px">
      {isFetching ? (
        <>
          <Skeleton data-testid="rider-info-skeleton-loader" variant="circular" width={24} height={24} />
          <Skeleton variant="text" width={139} height={32} />
          <Skeleton variant="circular" width={24} height={24} />
        </>
      ) : (
        children
      )}
    </Box>
  );
};
